import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Chip,
  Autocomplete,
  TextField,
  Button,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Info,
  CalendarMonth,
  Assessment,
  FactCheck,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { projectionsAnalysisService } from '../../../services/projectionsAnalysisService';
import { adminService } from '../../../services/adminService';
import { useSnackbar } from 'notistack';
import AccuracyMetricsCard from './AccuracyMetricsCard';
import { exportProjectionsAnalysisToHTML } from '../../../utils/projectionsAnalysisExport';

const ALL_EDC_OPTION = { id: null, name: 'All EDC schools' };

const SchoolAnalysisView = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [teams, setTeams] = useState([ALL_EDC_OPTION]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(ALL_EDC_OPTION);
  
  // School selection state
  const [schools, setSchools] = useState([]);
  const [selectedSchoolUuid, setSelectedSchoolUuid] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Get selected school name
  const getSelectedSchoolName = () => {
    const school = schools.find(s => s.school_uuid === selectedSchoolUuid);
    return school?.name || 'Unknown School';
  };

  // Handle HTML export
  const handleExportHTML = async () => {
    if (!analysis || !selectedTeam || !selectedSchoolUuid) {
      enqueueSnackbar('Please select a team and school first', { variant: 'warning' });
      return;
    }

    setExporting(true);
    try {
      const schoolName = getSelectedSchoolName();
      const teamName = selectedTeam.name || 'Unknown Team';
      
      // Export to HTML (no chart images for now, can be enhanced later)
      exportProjectionsAnalysisToHTML(schoolName, teamName, analysis, {});
      
      enqueueSnackbar('Analysis exported to HTML successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error exporting to HTML:', err);
      enqueueSnackbar('Failed to export analysis', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // Load teams on mount
  useEffect(() => {
    const loadAllTeams = async () => {
      setLoadingTeams(true);
      try {
        const response = await adminService.listTeams(0, 1000, enqueueSnackbar, true);
        const teamsData = response?.teams || [];
        setTeams([
          ALL_EDC_OPTION,
          ...teamsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        ]);
      } catch (err) {
        console.error('Error loading teams:', err);
        enqueueSnackbar('Failed to load teams', { variant: 'error' });
        setTeams([ALL_EDC_OPTION]);
      } finally {
        setLoadingTeams(false);
      }
    };
    
    loadAllTeams();
  }, [enqueueSnackbar]);
  
  // Load schools for selected team (or all EDC schools when no team is selected)
  useEffect(() => {
    const loadSchoolsForTeam = async () => {
      setSchoolsLoading(true);
      try {
        const teamId = selectedTeam?.id || null;
        const backtestResponse = await projectionsAnalysisService.listSchools(teamId);
        const backtestSchools = backtestResponse.schools || [];
        
        const filteredSchools = backtestSchools
          .map(s => ({
            school_uuid: s.school_uuid,
            name: s.school_name
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        setSchools(filteredSchools);
        
        if (filteredSchools.length > 0) {
          setSelectedSchoolUuid(filteredSchools[0].school_uuid);
        } else {
          setSelectedSchoolUuid('');
        }
      } catch (err) {
        console.error('Error loading team schools:', err);
        setSchools([]);
        setSelectedSchoolUuid('');
      } finally {
        setSchoolsLoading(false);
      }
    };

    loadSchoolsForTeam();
  }, [selectedTeam?.id, enqueueSnackbar]);

  // Load detailed analysis when school is selected
  useEffect(() => {
    if (!selectedSchoolUuid) return;

    const fetchAnalysis = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await projectionsAnalysisService.getSchoolDetailedAnalysis(selectedSchoolUuid);
        setAnalysis(data);
      } catch (err) {
        console.error('Error fetching school analysis:', err);
        setError(err.message || 'Failed to load school analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [selectedSchoolUuid]);

  if (loadingTeams) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        School Projection Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Detailed walkthrough of how backtesting validates projection accuracy
      </Typography>

      {/* Team and School Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Team Selector */}
          <Grid item xs={12} md={5}>
            <Autocomplete
              value={selectedTeam}
              onChange={(event, newValue) => setSelectedTeam(newValue || ALL_EDC_OPTION)}
              options={teams}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              loading={loadingTeams}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by team (optional)"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTeams ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          {/* School Selector */}
          <Grid item xs={12} md={5}>
            <FormControl fullWidth disabled={schoolsLoading}>
              <InputLabel>Select School</InputLabel>
              <Select
                value={selectedSchoolUuid}
                onChange={(e) => setSelectedSchoolUuid(e.target.value)}
                label="Select School"
              >
                {schools.length === 0 && !schoolsLoading ? (
                  <MenuItem disabled value="">
                    No schools with backtest data
                  </MenuItem>
                ) : (
                  schools.map((school) => (
                    <MenuItem key={school.school_uuid} value={school.school_uuid}>
                      {school.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Export Button */}
          <Grid item xs={12} md={2}>
            <Tooltip title={!analysis ? "Select a school to enable export" : "Export analysis as HTML"}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportHTML}
                  disabled={!analysis || loading || exporting}
                  sx={{ 
                    height: '56px',
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export HTML'}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        
        {schools.length === 0 && !schoolsLoading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {selectedTeam?.id
              ? 'No schools with backtest data found for this team. Switch to “All EDC schools” to see schools that have been backtested, or run backtests first.'
              : 'No schools with backtest data found. Run backtests first to see detailed analysis.'}
          </Alert>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && analysis && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <AccuracyMetricsCard
                title="Overall MAPE"
                icon={TrendingUp}
                value={analysis.summary.overall_mape || 0}
                subtitle="Midpoint Scenario"
                suffix="%"
                color={analysis.summary.overall_mape < 10 ? 'success' : analysis.summary.overall_mape < 25 ? 'warning' : 'error'}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AccuracyMetricsCard
                title="Years of Data"
                icon={CalendarMonth}
                value={analysis.summary.years_of_data || 0}
                subtitle="Historical enrollment"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AccuracyMetricsCard
                title="Backtest Projections"
                icon={Assessment}
                value={analysis.summary.total_projections || 0}
                subtitle="Scenarios tested"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <AccuracyMetricsCard
                title="Accuracy Records"
                icon={FactCheck}
                value={analysis.summary.total_accuracy_records || 0}
                subtitle="Comparisons made"
              />
            </Grid>
          </Grid>

          {/* Confidence Intervals Card */}
          {analysis.confidence_intervals && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp /> Statistical Confidence Intervals
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Based on this school's historical projection accuracy
                    </Typography>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Projection Horizon</TableCell>
                            <TableCell align="center">80% Confidence</TableCell>
                            <TableCell align="center">85% Confidence</TableCell>
                            <TableCell align="center">90% Confidence</TableCell>
                            <TableCell align="center">95% Confidence</TableCell>
                            <TableCell>Samples</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[1, 2, 3, 4, 5].map(yearsAhead => {
                            const ci = analysis.confidence_intervals[yearsAhead];
                            if (!ci || ci.sample_size < 2) return null;
                            
                            return (
                              <TableRow key={yearsAhead}>
                                <TableCell><strong>{yearsAhead}-Year</strong></TableCell>
                                <TableCell align="center">±{ci.ci_80}%</TableCell>
                                <TableCell align="center">±{ci.ci_85}%</TableCell>
                                <TableCell align="center">±{ci.ci_90}%</TableCell>
                                <TableCell align="center">±{ci.ci_95}%</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={`${ci.sample_size} samples`}
                                    size="small"
                                    color={ci.sample_size >= 10 ? 'success' : 'warning'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>How to read confidence intervals:</strong> An 80% confidence interval of ±15% means that 80% of historical projections 
                        fell within 15% of actual enrollment. Higher confidence levels (90%, 95%) provide wider intervals for more conservative planning. 
                        Samples &lt; 10 may have less reliable confidence estimates.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Historical Enrollment Data */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" />
                Historical Enrollment by Grade
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Grade-level enrollment shows phasing patterns, growth trajectories, and data stability:
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell><strong>Grade</strong></TableCell>
                      {Object.keys(analysis.enrollment_history)
                        .sort()
                        .map((year) => (
                          <TableCell key={year} align="center">
                            <strong>{year}</strong>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysis.grades && analysis.grades.map((gradeCode) => {
                      const firstYear = Object.keys(analysis.enrollment_history).sort()[0];
                      const gradeLabel = analysis.enrollment_history[firstYear]?.grades[gradeCode]?.label || gradeCode;
                      
                      return (
                        <TableRow key={gradeCode}>
                          <TableCell><strong>{gradeLabel}</strong></TableCell>
                          {Object.entries(analysis.enrollment_history)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([year, data]) => {
                              const gradeData = data.grades[gradeCode];
                              return (
                                <TableCell key={year} align="center">
                                  {gradeData ? (
                                    <Box sx={{ 
                                      display: 'inline-block',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      backgroundColor: gradeData.enrollment > 0 ? 'success.light' : 'grey.200',
                                      color: gradeData.enrollment > 0 ? 'success.dark' : 'text.disabled',
                                      minWidth: '40px'
                                    }}>
                                      {gradeData.enrollment}
                                    </Box>
                                  ) : (
                                    <Box sx={{ color: 'text.disabled' }}>—</Box>
                                  )}
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      );
                    })}
                    {/* Total row */}
                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                      <TableCell><strong>Total</strong></TableCell>
                      {Object.entries(analysis.enrollment_history)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([year, data]) => (
                          <TableCell key={year} align="center">
                            <strong>{data.total}</strong>
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Phasing indicator */}
              {analysis.grades && analysis.grades.length > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>How to read this table:</strong> Empty cells (—) indicate grades not offered that year. 
                    Look for patterns like grades being added over time (phasing in) or removed (phasing out). 
                    Green-highlighted cells show active enrollment.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Backtest Scenarios */}
          {analysis.backtest_scenarios.map((scenario, index) => (
            <Card key={scenario.projection_id} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TrendingUp color="primary" />
                  Backtest Scenario #{index + 1}
                  {scenario.mape !== null && (
                    <Chip 
                      label={`MAPE: ${scenario.mape}%`}
                      color={scenario.mape < 10 ? 'success' : scenario.mape < 25 ? 'warning' : 'error'}
                      size="small"
                    />
                  )}
                  {scenario.grade_stability && (
                    <Chip
                      label={scenario.grade_stability}
                      color={scenario.grade_stability === 'Stable' ? 'success' : 'warning'}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Setup:</strong> Used {scenario.source_data_years.length} years of data ({scenario.source_data_years[0].split('-')[0]}-{scenario.source_data_years[scenario.source_data_years.length - 1].split('-')[0]})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Entry Grade:</strong> {scenario.entry_grade}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Grade Stability:</strong> {scenario.grade_stability === 'Stable' 
                      ? 'School offered the same grades throughout this period' 
                      : scenario.grade_stability === 'Phasing In/Out'
                        ? 'School added or removed grades during this period'
                        : 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Simulation:</strong> "If we made projections using only this historical data, how accurate would they be?"
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Time Series Visualization */}
                {(() => {
                  // Prepare time series data
                  const timeSeriesData = [];
                  const allYears = new Set();
                  
                  // Collect all years
                  ['min', 'median', 'max'].forEach(scenarioType => {
                    scenario.accuracy_by_scenario[scenarioType]?.forEach(record => {
                      allYears.add(record.year);
                    });
                  });
                  
                  // Sort years
                  const sortedYears = Array.from(allYears).sort();
                  
                  // Build data structure
                  sortedYears.forEach(year => {
                    const minRecord = scenario.accuracy_by_scenario.min?.find(r => r.year === year);
                    const medianRecord = scenario.accuracy_by_scenario.median?.find(r => r.year === year);
                    const maxRecord = scenario.accuracy_by_scenario.max?.find(r => r.year === year);
                    
                    if (medianRecord) {
                      timeSeriesData.push({
                        year,
                        min: minRecord?.projected || null,
                        median: medianRecord.projected,
                        max: maxRecord?.projected || null,
                        actual: medianRecord.actual,
                        withinBounds: medianRecord.actual >= (minRecord?.projected || 0) && 
                                      medianRecord.actual <= (maxRecord?.projected || Infinity)
                      });
                    }
                  });
                  
                  return timeSeriesData.length > 0 ? (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="primary" fontSize="small" /> Projected vs Actual Enrollment Over Time
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="year" 
                            tick={{ fontSize: 13 }}
                            axisLine={{ stroke: '#e0e0e0' }}
                          />
                          <YAxis 
                            label={{ 
                              value: 'Enrollment', 
                              angle: -90, 
                              position: 'insideLeft',
                              style: { fontSize: 13, fill: '#64748b' }
                            }}
                            tick={{ fontSize: 12 }}
                            axisLine={{ stroke: '#e0e0e0' }}
                          />
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <Paper sx={{ p: 2, boxShadow: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {data.year}
                                    </Typography>
                                    <Typography variant="body2">Low: {data.min?.toLocaleString()}</Typography>
                                    <Typography variant="body2">Midpoint: {data.median?.toLocaleString()}</Typography>
                                    <Typography variant="body2">High: {data.max?.toLocaleString()}</Typography>
                                    <Typography variant="body2" sx={{ color: data.withinBounds ? 'success.main' : 'error.main', fontWeight: 600, mt: 0.5 }}>
                                      Actual: {data.actual?.toLocaleString()} {data.withinBounds ? '✓' : '✗'}
                                    </Typography>
                                  </Paper>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />
                          <Area 
                            type="monotone" 
                            dataKey="max" 
                            fill="#e3f2fd" 
                            stroke="none" 
                            name="High Projection"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="min" 
                            fill="#fff" 
                            stroke="none" 
                            name="Low Projection"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="median" 
                            stroke="#4338ca" 
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            name="Midpoint Projection"
                            dot={false}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="actual" 
                            stroke="#4caf50" 
                            strokeWidth={3}
                            name="Actual Enrollment"
                            dot={{ r: 5, fill: '#4caf50' }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        The shaded area represents the projected min/max range. Green line shows actual enrollment.
                      </Typography>
                    </Box>
                  ) : null;
                })()}

                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Projection Results (All Scenarios)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell><strong>School Year</strong></TableCell>
                        <TableCell align="right"><strong>Low</strong></TableCell>
                        <TableCell align="right"><strong>Midpoint</strong></TableCell>
                        <TableCell align="right"><strong>High</strong></TableCell>
                        <TableCell align="right"><strong>Actual</strong></TableCell>
                        <TableCell align="center"><strong>Within Bounds?</strong></TableCell>
                        <TableCell align="right"><strong>Midpoint % Error</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scenario.accuracy_by_scenario.median.length > 0 ? (
                        scenario.accuracy_by_scenario.median.map((medianRecord) => {
                          // Find corresponding min and max records
                          const minRecord = scenario.accuracy_by_scenario.min.find(
                            r => r.year === medianRecord.year
                          );
                          const maxRecord = scenario.accuracy_by_scenario.max.find(
                            r => r.year === medianRecord.year
                          );
                          
                          // Determine if actual is within bounds
                          const withinBounds = minRecord && maxRecord && medianRecord.actual >= minRecord.projected && medianRecord.actual <= maxRecord.projected;
                          
                          return (
                            <TableRow key={medianRecord.year || medianRecord.target_year}>
                              <TableCell>
                                <strong>{medianRecord.year}</strong>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ color: 'primary.dark' }}>
                                  {minRecord?.projected || '—'}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <strong>{medianRecord.projected}</strong>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ color: 'primary.dark' }}>
                                  {maxRecord?.projected || '—'}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ 
                                  fontWeight: 'bold',
                                  color: withinBounds ? 'success.main' : 'warning.main'
                                }}>
                                  {medianRecord.actual}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {minRecord && maxRecord ? (
                                  withinBounds ? (
                                    <Chip 
                                      icon={<CheckCircle />} 
                                      label="Yes" 
                                      color="success" 
                                      size="small"
                                    />
                                  ) : (
                                    <Chip 
                                      label="No" 
                                      color="warning" 
                                      size="small"
                                    />
                                  )
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                  {medianRecord.percent_error !== null && (
                                    <>
                                      {Math.abs(medianRecord.percent_error) < 5 && <CheckCircle fontSize="small" color="success" />}
                                      {Math.abs(medianRecord.percent_error).toFixed(2)}%
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No accuracy records available for this projection
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>How to read this table:</strong> The <strong>Low</strong> and <strong>High</strong> columns show the projected range (min/max scenarios). 
                    The <strong>Actual</strong> enrollment should ideally fall within this range. Green actual values indicate they were within bounds; 
                    orange indicates they fell outside the predicted range.
                  </Typography>
                </Box>

                {scenario.mape !== null && scenario.accuracy_by_scenario.median.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>MAPE Calculation (Midpoint Scenario):</strong> Average of absolute % errors = (
                      {scenario.accuracy_by_scenario.median
                        .filter(r => r.percent_error !== null)
                        .map(r => `${Math.abs(r.percent_error).toFixed(2)}%`)
                        .join(' + ')}
                      ) / {scenario.accuracy_by_scenario.median.filter(r => r.percent_error !== null).length} = <strong>{scenario.mape}%</strong>
                    </Typography>
                  </Box>
                )}
                
                {scenario.accuracy_by_scenario.min.length > 0 && scenario.accuracy_by_scenario.max.length > 0 && (
                  <Box sx={{ mt: 1, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Within Bounds Rate:</strong> {
                        (() => {
                          const withinBoundsCount = scenario.accuracy_by_scenario.median.filter(medianRecord => {
                            const minRecord = scenario.accuracy_by_scenario.min.find(r => r.year === medianRecord.year);
                            const maxRecord = scenario.accuracy_by_scenario.max.find(r => r.year === medianRecord.year);
                            return minRecord && maxRecord && medianRecord.actual >= minRecord.projected && medianRecord.actual <= maxRecord.projected;
                          }).length;
                          const totalCount = scenario.accuracy_by_scenario.median.length;
                          const rate = totalCount > 0 ? ((withinBoundsCount / totalCount) * 100).toFixed(1) : 0;
                          return (
                            <>
                              {withinBoundsCount} out of {totalCount} projections ({rate}%) had actual enrollment within the Low/High range. 
                              {rate >= 80 ? ' Excellent confidence interval coverage!' : rate >= 60 ? ' Good coverage, consider reviewing outliers.' : ' Low coverage suggests bounds may need adjustment.'}
                            </>
                          );
                        })()
                      }
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Key Insights */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Insights
              </Typography>
              
              {analysis.summary.overall_mape < 10 ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Exceptional Accuracy (MAPE &lt; 10%)
                  </Typography>
                  <Typography variant="body2">
                    This school shows very stable enrollment patterns. The projection model performs excellently, 
                    with typical errors of less than {analysis.summary.overall_mape}%. This level of accuracy is 
                    highly actionable for planning purposes.
                  </Typography>
                </Alert>
              ) : analysis.summary.overall_mape < 25 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Good Accuracy (MAPE 10-25%)
                  </Typography>
                  <Typography variant="body2">
                    The projections show reasonable accuracy with an average error of {analysis.summary.overall_mape}%. 
                    This is suitable for medium-term planning but should be updated regularly as new data becomes available.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Lower Accuracy (MAPE &gt; 25%)
                  </Typography>
                  <Typography variant="body2">
                    With MAPE of {analysis.summary.overall_mape}%, this school shows more volatile or unpredictable enrollment patterns. 
                    Projections should be used with caution and supplemented with qualitative insights about school-specific factors.
                  </Typography>
                </Alert>
              )}

              <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                Why These Results Matter:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" paragraph>
                  <strong>Backtesting validates the model:</strong> By simulating past projections and comparing to reality, 
                  we can quantify exactly how accurate our predictions were.
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  <strong>School-specific performance:</strong> The model works better for some schools than others, 
                  helping identify which projections to trust more.
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Shorter timeframes are more accurate:</strong> Projections 1-2 years out are typically much more 
                  reliable than 4-5 year projections.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default SchoolAnalysisView;
